import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { SecurityIssuesList } from "@/components/security/SecurityIssuesList";
import { IssueSeverityCard } from "@/components/security/IssueSeverityCard";
import { IssueStatusTrendChart } from "@/components/security/IssueStatusTrendChart";
import { OpenIssuesTrendCard } from "@/components/security/OpenIssuesTrendCard";
import { SeverityLevel, DateRange, IssueStatus, IssueType } from "@/types/security";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import { Filter, X, AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { PageLoader } from "@/components/ui/page-loader";
import { Skeleton } from "@/components/ui/skeleton";
import { useSecurityIssues, useSecurityIssueStats } from "@/hooks/useSecurityIssues";
import {
  transformToSeverityData,
  transformToOpenIssueAgeData,
  transformToStatusTrendData,
} from "@/utils/securityHelpers";

export function IssuesPage() {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<DateRange>("30");
  const [page, setPage] = useState(1);
  const [severityFilter, setSeverityFilter] = useState<SeverityLevel | undefined>();
  const [statusFilter, setStatusFilter] = useState<IssueStatus | undefined>();
  const [typeFilter, setTypeFilter] = useState<IssueType | undefined>();

  // Get days from dateRange for stats query
  const statsDays = parseInt(dateRange, 10);

  const {
    data: issuesResponse,
    isLoading,
    error,
  } = useSecurityIssues({
    page,
    items_per_page: 20,
    severity: severityFilter,
    status: statusFilter,
    issue_type: typeFilter,
  });

  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError,
  } = useSecurityIssueStats(statsDays);

  const issues = issuesResponse?.data ?? [];
  const totalCount = issuesResponse?.total_count ?? 0;

  // Transform stats data for UI components
  const issueSeverityData = useMemo(() => transformToSeverityData(stats), [stats]);
  const openIssueAgeData = useMemo(() => transformToOpenIssueAgeData(stats), [stats]);
  const statusTrendData = useMemo(() => transformToStatusTrendData(stats), [stats]);

  const handleIssueClick = useCallback(
    (issue: { id: string }) => {
      navigate(`/issues/${issue.id}`);
    },
    [navigate]
  );

  const handleSeverityClick = (severity: SeverityLevel) => {
    setSeverityFilter(severity);
    setPage(1); // Reset to first page when filter changes
  };

  const handleOpenIssuesClick = () => {
    setStatusFilter("OPEN");
    setSeverityFilter("CRITICAL");
    setPage(1);
  };

  const handleClearFilters = () => {
    setSeverityFilter(undefined);
    setStatusFilter(undefined);
    setTypeFilter(undefined);
    setPage(1);
  };

  const toggleSeverityFilter = (severity: SeverityLevel) => {
    setSeverityFilter((prev) => (prev === severity ? undefined : severity));
    setPage(1);
  };

  const toggleStatusFilter = (status: IssueStatus) => {
    setStatusFilter((prev) => (prev === status ? undefined : status));
    setPage(1);
  };

  const toggleTypeFilter = (type: IssueType) => {
    setTypeFilter((prev) => (prev === type ? undefined : type));
    setPage(1);
  };

  const hasActiveFilters = severityFilter !== undefined || statusFilter !== undefined || typeFilter !== undefined;

  // Loading state
  if (isLoading) {
    return <PageLoader message="Loading security issues..." />;
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-6">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
              <div>
                <h3 className="font-semibold text-destructive">Error</h3>
                <p className="text-sm text-muted-foreground">
                  Failed to load security issues. Please try again later.
                </p>
                {error instanceof Error && (
                  <p className="text-xs text-muted-foreground mt-1">{error.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-4 p-6">
      {statsError && (
        <Card className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-yellow-800 dark:text-yellow-200">
              <AlertCircle className="h-4 w-4" />
              <span>
                Failed to load statistics. Some charts may show incomplete data.
                {statsError instanceof Error && ` ${statsError.message}`}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Three cards side by side */}
      <div className="grid gap-4 md:grid-cols-3">
        {statsLoading ? (
          <>
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-32" />
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-40" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-[220px] w-full" />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-36" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-32 w-full" />
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            <IssueSeverityCard data={issueSeverityData} onSeverityClick={handleSeverityClick} />
            <IssueStatusTrendChart
              data={statusTrendData}
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
            />
            <OpenIssuesTrendCard data={openIssueAgeData} onClick={handleOpenIssuesClick} />
          </>
        )}
      </div>

      {/* Security Issues Table with Filters */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-base">
              Security Issues {totalCount > 0 && `(${totalCount.toLocaleString()} total)`}
            </CardTitle>
            {hasActiveFilters && (
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {severityFilter && (
                  <Badge variant="secondary" className="text-xs flex items-center gap-1">
                    {severityFilter}
                    <button
                      type="button"
                      aria-label={`Clear ${severityFilter} severity filter`}
                      onClick={() => toggleSeverityFilter(severityFilter)}
                      className="ml-1 h-3 w-3 cursor-pointer hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary rounded"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                {statusFilter && (
                  <Badge variant="secondary" className="text-xs flex items-center gap-1">
                    {statusFilter}
                    <button
                      type="button"
                      aria-label={`Clear ${statusFilter} status filter`}
                      onClick={() => toggleStatusFilter(statusFilter)}
                      className="ml-1 h-3 w-3 cursor-pointer hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary rounded"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                {typeFilter && (
                  <Badge variant="secondary" className="text-xs flex items-center gap-1">
                    {typeFilter}
                    <button
                      type="button"
                      aria-label={`Clear ${typeFilter} type filter`}
                      onClick={() => toggleTypeFilter(typeFilter)}
                      className="ml-1 h-3 w-3 cursor-pointer hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary rounded"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearFilters}
                  className="h-6 px-2 text-xs"
                >
                  Clear all
                </Button>
              </div>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                Filter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5 text-sm font-semibold">Severity</div>
              <DropdownMenuCheckboxItem
                checked={severityFilter === "CRITICAL"}
                onCheckedChange={() => toggleSeverityFilter("CRITICAL")}
              >
                Critical
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={severityFilter === "HIGH"}
                onCheckedChange={() => toggleSeverityFilter("HIGH")}
              >
                High
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={severityFilter === "MEDIUM"}
                onCheckedChange={() => toggleSeverityFilter("MEDIUM")}
              >
                Medium
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={severityFilter === "LOW"}
                onCheckedChange={() => toggleSeverityFilter("LOW")}
              >
                Low
              </DropdownMenuCheckboxItem>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5 text-sm font-semibold">Status</div>
              <DropdownMenuCheckboxItem
                checked={statusFilter === "OPEN"}
                onCheckedChange={() => toggleStatusFilter("OPEN")}
              >
                Open
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={statusFilter === "IN_REVIEW"}
                onCheckedChange={() => toggleStatusFilter("IN_REVIEW")}
              >
                In Review
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={statusFilter === "RESOLVED"}
                onCheckedChange={() => toggleStatusFilter("RESOLVED")}
              >
                Resolved
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={statusFilter === "FALSE_POSITIVE"}
                onCheckedChange={() => toggleStatusFilter("FALSE_POSITIVE")}
              >
                False Positive
              </DropdownMenuCheckboxItem>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5 text-sm font-semibold">Type</div>
              <DropdownMenuCheckboxItem
                checked={typeFilter === "PII_DETECTION"}
                onCheckedChange={() => toggleTypeFilter("PII_DETECTION")}
              >
                PII Detection
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={typeFilter === "PROMPT_INJECTION"}
                onCheckedChange={() => toggleTypeFilter("PROMPT_INJECTION")}
              >
                Prompt Injection
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={typeFilter === "DATA_EXFILTRATION"}
                onCheckedChange={() => toggleTypeFilter("DATA_EXFILTRATION")}
              >
                Data Exfiltration
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={typeFilter === "TOXIC_CONTENT"}
                onCheckedChange={() => toggleTypeFilter("TOXIC_CONTENT")}
              >
                Toxic Content
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={typeFilter === "MALICIOUS_URL"}
                onCheckedChange={() => toggleTypeFilter("MALICIOUS_URL")}
              >
                Malicious URL
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={typeFilter === "SECRETS_EXPOSURE"}
                onCheckedChange={() => toggleTypeFilter("SECRETS_EXPOSURE")}
              >
                Secrets Exposure
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </CardHeader>
        <CardContent className="pt-0">
          {issues.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <AlertCircle className="h-12 w-12 mb-4 text-muted-foreground opacity-50" />
              <p className="text-sm font-medium mb-1">No issues found</p>
              <p className="text-xs text-muted-foreground">
                {hasActiveFilters
                  ? "Try adjusting your filters"
                  : "No security issues detected yet"}
              </p>
            </div>
          ) : (
            <>
              <SecurityIssuesList issues={issues} onIssueClick={handleIssueClick} />
              {/* Pagination */}
              {totalCount > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 pt-4 border-t">
                  <div className="text-xs text-muted-foreground">
                    Showing {issues.length} of {totalCount.toLocaleString()} issues
                    {totalCount > 20 &&
                      ` (Page ${page} of ${Math.ceil(totalCount / 20)})`}
                  </div>
                  {totalCount > 20 && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page === 1 || isLoading}
                        onClick={() => {
                          setPage((p) => Math.max(1, p - 1));
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        className="text-xs sm:text-sm min-w-[80px]"
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page >= Math.ceil(totalCount / 20) || isLoading}
                        onClick={() => {
                          setPage((p) => p + 1);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        className="text-xs sm:text-sm min-w-[80px]"
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
